import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CompanyApi } from "../../../../../services/phpServices";
import { CustomListItem } from "../../../components/ListItem";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useStores } from "../../../../../models/helpers/useStores";
import { Colors } from "../../../../../theme";
import { useTranslation } from "react-i18next";

interface NewCategoryProps {
  open: boolean;
  onClose?: () => void;
  id: number;
}

const CategoryModal: React.FC<NewCategoryProps> = observer(
  ({ open, onClose, id }) => {
    const [categoryName, setcategoryName] = useState("");
    const [isFocusedDes, setIsFocusedDes] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
      templateStore: { getListTaskCategories, categories },
    } = useStores();
    const { t } = useTranslation() as any;

    const handleChange = (value: string) => {
      setcategoryName(value);
    };
    const handleClose = () => {
      if (onClose) onClose();
    };
    const deleteCate = async () => {
      setLoading(true);
      try {
        await new CompanyApi().deleteCategory(id);
        getListTaskCategories();
        handleClose();
      } catch (e) {
        console.error("Delete failed:", e);
      } finally {
        setLoading(false);
      }
    };

    const handleConfirm = async () => {
      if (!categoryName.trim()) return;
      setLoading(true);
      try {
        if (id === 0) {
          await new CompanyApi().createCategory({
            type: "TASKS",
            name: categoryName,
          });
        } else {
          await new CompanyApi().updateCategory(id, {
            type: "TASKS",
            name: categoryName,
          });
        }
        getListTaskCategories();
        handleClose();
      } catch (e) {
        console.error("Failed to save category:", e);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (id !== 0) {
        const categoryName = categories.find((c) => c.category_id === id)?.name;
        setcategoryName(categoryName ?? "");
      }
    }, [id]);

    return (
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: 24,
            width: "500px",
            maxHeight: "80vh",
            transform: "t(-50%, -50%)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: "40px",
              backgroundColor: "#08B0A0",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px 4px 0 0",
              justifyContent: "space-between",
            }}
          >
            <Typography
              color={Colors.white}
              sx={{ paddingLeft: "16px" }}
              variant="h5"
            >
              {id === 0
                ? t("00_03_new").toUpperCase()
                : t("00_03_edit").toUpperCase()}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ color: "white", width: 24, height: 24 }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingBottom: "20px",
              overflowY: "auto",
              maxHeight: "calc(80vh - 40px)",
              marginTop: "8px",
            }}
          >
            <CustomListItem
              leftContainerWidth={150}
              text={`${t("26_00_name")} *`}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <TextField
                  id="category-name"
                  label={t("00_06_name")}
                  variant="standard"
                  inputProps={{ maxLength: 200 }}
                  style={{ width: "100%" }}
                  onFocus={() => setIsFocusedDes(true)}
                  onBlur={(e) => {
                    if (!e.target.value) setIsFocusedDes(false);
                  }}
                  value={categoryName}
                  onChange={(e) => handleChange(e.target.value)}
                  InputProps={{
                    style: { fontSize: "13px", fontWeight: "400" },
                  }}
                  InputLabelProps={{
                    style: { fontSize: "13px", fontWeight: "400" },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "13px",
                      fontWeight: "400",
                      opacity: isFocusedDes ? 0 : 1,
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "13px",
                      fontWeight: "400",
                    },
                  }}
                />
              }
            />
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              justifyContent: "flex-end",
              display: "flex",
              marginTop: "40px",
              paddingRight: "20px",
              paddingBottom: "20px",
              flexDirection: "row",
              gap: "20px",
            }}
          >
            {id !== 0 && (
              <Button
                variant="outlined"
                style={{
                  color: "rgb(255, 100, 100)",
                  borderColor: "rgb(255, 150, 150)",
                  fontSize: "13px",
                  maxHeight: "36px",
                }}
                onClick={deleteCate}
              >
                {t("00_03_delete")}
              </Button>
            )}
            <Button
              variant="outlined"
              style={{
                color: "gray",
                borderColor: "gray",
                fontSize: "13px",
                maxHeight: "36px",
              }}
              onClick={handleClose}
            >
              {t("00_03_cancel")}
            </Button>
            <Button
              variant="contained"
              style={{ fontSize: "13px", maxHeight: "36px" }}
              onClick={handleConfirm}
              disabled={!categoryName.trim() || loading}
            >
              {t("00_03_save")}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
);

export default CategoryModal;
